"use client";

import Link from "next/link";
import { ReactNode } from "react";

interface ButtonProps {
  variant?: "primary" | "secondary";
  href?: string;
  onClick?: () => void;
  children: ReactNode;
  className?: string;
  disabled?: boolean;
}

export default function Button({
  variant = "primary",
  href,
  onClick,
  children,
  className = "",
  disabled = false,
  ...buttonProps
}: ButtonProps) {
  const primaryStyles = `
    inline-flex items-center justify-center
    bg-colorBaseGray850 hover:bg-[#1a242f] 
    text-white hover:text-white 
    text-normal
    px-[26px] py-[10px]
    rounded-full min-w-[120px] 
    hover:shadow-button-hover active:shadow-button-active
    focus:outline-none focus-visible:outline-2 focus-visible:outline-blue-600 focus-visible:outline-offset-2
    transition-all duration-300 ease-in-out
    disabled:bg-gray-200 disabled:text-gray-400 disabled:border-gray-200 disabled:cursor-default disabled:shadow-none
    dark:disabled:bg-gray-800 dark:disabled:text-gray-600
    ${className}
  `;

  const secondaryStyles = `
    inline-flex items-center justify-center
    text-black hover:text-black 
    text-normal
    px-[26px] py-[10px]
    rounded-full min-w-[120px] 
    hover:shadow-button-hover active:shadow-button-active
    focus:outline-none focus-visible:outline-2 focus-visible:outline-blue-600 focus-visible:outline-offset-2
    transition-all duration-300 ease-in-out
    border-2 border-black
    dark:disabled:bg-gray-800 dark:disabled:text-gray-600
    ${className}
  `;

  if (href) {
    return (
      <Link
        href={href}
        className={variant === "primary" ? primaryStyles : secondaryStyles}
        {...buttonProps}
      >
        {children}
      </Link>
    );
  }

  return (
    <button
      onClick={onClick}
      className={variant === "primary" ? primaryStyles : secondaryStyles}
      disabled={disabled}
      {...buttonProps}
    >
      {children}
    </button>
  );
}
